
import {
  defineComponent,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import type { SpmColumnConfig } from '@/components/table-rest-api/types';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  fetchSystemSchemas,
  SchemaItem,
} from '@/composables/custom-data/customData';

interface DataSource {
  id_data_source: number;
  label: string;
  type: string;
  is_default: boolean;
  active: boolean;
  parent_id: number | null;
}

export default defineComponent({
  name: 'SystemObjectsList',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmOverlayPanel,
    SpmBadge,
    Button,
    ProgressSpinner,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const tableRef = ref();

    // System schemas table
    const tableData = ref<SchemaItem[]>([]);

    // Data sources overlay
    const dataSources = ref<DataSource[]>([]);
    const sourcesLoading = ref(true);

    const typeColors: Record<string, { bg: string; color: string }> = {
      ecommerce_connector: { bg: '#dcfce7', color: '#166534' },
      api: { bg: '#e0e7ff', color: '#3730a3' },
      manual: { bg: '#fef3c7', color: '#92400e' },
    };

    const childSet = ref<Set<number>>(new Set());

    const hasChildren = (id: number): boolean => childSet.value.has(id);

    const fetchDataSources = async () => {
      sourcesLoading.value = true;
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/data-sources',
          {},
          '',
          undefined,
          true,
        );
        if (response && response.data) {
          dataSources.value = response.data;
          const parents = new Set<number>();
          response.data.forEach((ds: DataSource) => {
            if (ds.parent_id) parents.add(ds.parent_id);
          });
          childSet.value = parents;
        }
      } finally {
        sourcesLoading.value = false;
      }
    };

    const fetchData = async () => {
      tableData.value = await fetchSystemSchemas(UserState.activeShop?.id ?? 0);
      return tableData.value;
    };

    const reloadTableData = async () => {
      if (tableRef.value) {
        await tableRef.value.reloadData();
      }
    };

    const navigateToSchema = (rowData: SchemaItem) => {
      router.push({ name: `shop.syncs.${rowData.type}` });
    };

    const columns: SpmColumnConfig[] = [
      {
        id: 'schema_name',
        header: t('myShopData.syncs.schema_name'),
        fields: [
          {
            name: 'schema_name',
            label: t('myShopData.syncs.schema_name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'last_date_sync',
        ],
      },
      {
        id: 'actions',
        header: '',
        fields: ['type'],
        maxWidth: '60px',
      },
    ];

    onMounted(() => {
      reloadTableData();
      fetchDataSources();
    });

    return {
      t,
      router,
      tableRef,
      tableData,
      fetchData,
      columns,
      navigateToSchema,
      dataSources,
      sourcesLoading,
      typeColors,
      hasChildren,
    };
  },
});
